import {
  MedicareBridgeCaseView,
  MedicareBridgeLinkedPrescription,
  MedicareBridgeLinkedPriorAuthorization,
} from './medicare-bridge.types';

const PRIOR_AUTH_STATUS_LABEL: Record<string, string> = {
  PENDING: 'Pending',
  COMPLETE: 'Complete',
  CANCELLED: 'Cancelled',
  NOT_SUBMITTED: 'Not submitted',
  FAILED: 'Failed',
};

const PRIOR_AUTH_OUTCOME_LABEL: Record<
  NonNullable<MedicareBridgeLinkedPriorAuthorization['outcome']>,
  string
> = {
  APPROVED: 'Approved',
  DENIED: 'Denied',
  OTHER: 'Other',
};

const PRESCRIPTION_STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'Active',
  PENDING: 'Pending',
  CANCELED: 'Canceled',
};

/** Stored review checklist first, then the live checklist. Raw code when neither lists the product. */
function productLabel(
  bridgeCase: MedicareBridgeCaseView,
  product: string,
): string {
  const checklists = [
    bridgeCase.latestReview?.checklist,
    bridgeCase.checklist,
  ];
  for (const checklist of checklists) {
    const label = checklist?.items
      .find((item) => item.key === 'requested_product')
      ?.options?.find((option) => option.value === product)?.label;
    if (label) return label;
  }
  return product;
}

function priorAuthLine(
  bridgeCase: MedicareBridgeCaseView,
  priorAuth: MedicareBridgeLinkedPriorAuthorization,
): string {
  const status =
    priorAuth.status === 'COMPLETE'
      ? priorAuth.outcome
        ? PRIOR_AUTH_OUTCOME_LABEL[priorAuth.outcome]
        : 'Complete'
      : (PRIOR_AUTH_STATUS_LABEL[priorAuth.status] ?? priorAuth.status);
  return `${productLabel(bridgeCase, priorAuth.product)} · ${status}`;
}

function prescriptionLine(prescription: MedicareBridgeLinkedPrescription): string {
  const status =
    PRESCRIPTION_STATUS_LABEL[prescription.status] ?? prescription.status;
  return `${prescription.drugDisplayName ?? 'Prescription'} · ${status}`;
}

/**
 * Records linked to the case. Status is the record's own status.
 * A link does not mean the payer approved the drug or the pharmacy dispensed it.
 */
export function MedicareBridgeLinkedRecords({
  bridgeCase,
}: {
  bridgeCase: MedicareBridgeCaseView;
}) {
  const priorAuthorizations = bridgeCase.priorAuthorizations ?? [];
  const prescriptions = bridgeCase.prescriptions ?? [];
  if (priorAuthorizations.length === 0 && prescriptions.length === 0) {
    return null;
  }

  return (
    <div
      className="flex flex-col gap-2 text-sm"
      data-testid="medicare-bridge-linked-records"
    >
      <p className="font-semibold text-foreground">Linked to this case</p>
      {priorAuthorizations.length > 0 ? (
        <div>
          <p className="text-muted-foreground">Prior authorizations</p>
          <ul className="text-foreground">
            {priorAuthorizations.map((priorAuth) => (
              <li key={priorAuth.id}>{priorAuthLine(bridgeCase, priorAuth)}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {prescriptions.length > 0 ? (
        <div>
          <p className="text-muted-foreground">Prescriptions</p>
          <ul className="text-foreground">
            {prescriptions.map((prescription) => (
              <li key={prescription.id}>{prescriptionLine(prescription)}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <p className="text-muted-foreground">
        A link is not payer approval and is not dispensing.
      </p>
    </div>
  );
}
